import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { LanguagePicker } from '../i18n/LanguagePicker.js';
import { CommandPalette } from './CommandPalette.js';
import { ConnectionBanner } from './ConnectionBanner.js';
import { LiveRegion } from './LiveRegion.js';

/**
 * The signed-in shell.
 *
 * Navigation lists the destinations the product is organised around (plan §8). Everything past
 * Today is disabled until the sprint that builds it — a link that goes nowhere is worse than one
 * that says "not yet", so unbuilt destinations are marked rather than hidden: it shows a user
 * where the product is going without pretending it has arrived.
 */

interface NavItem {
  readonly key: string;
  readonly to?: string;
  /**
   * Shown only to somebody who operates the deployment. Hiding it is courtesy, not a control: the
   * routes behind it answer 404 to everybody else regardless of what this list says.
   */
  readonly platformOnly?: boolean;
}

const NAVIGATION: readonly NavItem[] = [
  { key: 'nav.today', to: '/' },
  { key: 'nav.attention' },
  { key: 'nav.decisions', to: '/decisions' },
  { key: 'nav.allMail', to: '/mail' },
  { key: 'nav.people' },
  { key: 'nav.money', to: '/money' },
  { key: 'nav.family' },
  { key: 'nav.documents' },
  { key: 'nav.subscriptions', to: '/money' },
  { key: 'nav.waiting', to: '/waiting' },
  { key: 'nav.search', to: '/search' },
  { key: 'nav.butler', to: '/butler' },
  { key: 'nav.rescue', to: '/rescue' },
  { key: 'nav.mailboxes', to: '/settings/mailboxes' },
  { key: 'nav.vault' },
  { key: 'nav.activity', to: '/activity' },
  { key: 'nav.autonomy', to: '/settings/autonomy' },
  { key: 'security.title', to: '/settings/security' },
  { key: 'nav.admin', to: '/admin', platformOnly: true },
];

function WorkspaceSwitcher(): ReactNode {
  const { t } = useI18n();
  const { workspaces, activeWorkspace, selectWorkspace } = useAuth();
  const id = useId();

  if (workspaces.length <= 1) {
    return activeWorkspace === null ? null : <p className="workspace">{activeWorkspace.name}</p>;
  }

  return (
    <div className="field">
      <label htmlFor={id}>{t('workspace.switch')}</label>
      <select
        id={id}
        className="select"
        value={activeWorkspace?.id ?? ''}
        onChange={(event) => selectWorkspace(event.target.value)}
      >
        {workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }): ReactNode {
  const { t } = useI18n();
  const { platformRole } = useAuth();
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [currentPath]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);

    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('menu-open');
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  const navigation = NAVIGATION.filter(
    (item) => item.platformOnly !== true || platformRole !== null,
  );

  return (
    <div className="app">
      {/*
        The skip link says what it does, not where it goes.

        It read "Today" before, which is the name of a destination rather than an instruction, and a
        skip link somebody has to interpret is a skip link they pass over.
      */}
      <a className="skip-link" href="#main">
        {t('a11y.skipToContent')}
      </a>

      <LiveRegion />

      <header className="mobile-header">
        <img className="mobile-header__logo" src="/mailbutlerlogo-v2.png" alt={t('app.name')} />
        <button
          className="mobile-menu-button"
          type="button"
          aria-controls="primary-navigation"
          aria-expanded={menuOpen}
          aria-label={t(menuOpen ? 'menu.closeNavigation' : 'menu.openNavigation')}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </header>

      <button
        className={`sidebar-backdrop${menuOpen ? ' sidebar-backdrop--visible' : ''}`}
        type="button"
        aria-label={t('menu.closeNavigation')}
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />

      <nav
        id="primary-navigation"
        className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}
        aria-label={t('app.name')}
      >
        <button
          className="sidebar__close"
          type="button"
          aria-label={t('menu.closeNavigation')}
          onClick={() => setMenuOpen(false)}
        >
          <span aria-hidden="true">×</span>
        </button>
        <div className="brand">
          <img className="brand__logo" src="/mailbutlerlogo-v2.png" alt={t('app.name')} />
          <span className="brand__tagline">{t('app.tagline')}</span>
        </div>

        <WorkspaceSwitcher />

        <ul className="nav" role="list">
          {navigation.map((item) =>
            item.to === undefined ? (
              <li key={item.key}>
                <span className="nav__item nav__item--pending" aria-disabled="true">
                  {t(item.key)}
                </span>
              </li>
            ) : (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="nav__item"
                  onClick={() => setMenuOpen(false)}
                  {...(currentPath === item.to ? { 'aria-current': 'page' as const } : {})}
                >
                  {t(item.key)}
                </Link>
              </li>
            ),
          )}
        </ul>

        <div className="sidebar__footer">
          <LanguagePicker />
          <p className="shortcut-hint">
            <kbd>Ctrl</kbd> <kbd>K</kbd>
          </p>
        </div>
      </nav>

      <main className="main" id="main">
        {/* Above the content and sticky, never over it: the screen is still true, just not newer. */}
        <ConnectionBanner lastLoadedAt={null} />
        <div className="content">{children}</div>
      </main>

      <CommandPalette />
    </div>
  );
}
